import React from "react";
import { Grid, Typography } from "@mui/material";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import { HoldingRes } from "src/store/features/api/generated";
import { RootState, useAppSelector } from "src/store/store";
import formatCoinName from "src/utils/formatCoinName";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import GainsOrLoses from "src/components/common/GainsOrLoses";
import { HoldingsListStyles } from "../common/styles/HoldingsList.styles";
import { Holding, Holdings } from "../common/types/holdings";

interface HoldingsListProps {
  holdings: HoldingRes[] | undefined;
}

const HoldingsList = (props: HoldingsListProps): JSX.Element => {
  const { holdings } = props;
  const classes = HoldingsListStyles();

  // redux - get users selected currency
  const { selectedCurrency } = useAppSelector((state: RootState) => state.settings);

  const roundNumber = (num: number, d: number = 7): number => {
    return Math.floor(num * Math.pow(10, d)) / Math.pow(10, d);
  };

  // flatten shallow nested object
  const flatten = (ob: any) => {
    let result: any = {};

    for (const i in ob) {
      // check if change is null to then handle value correctly in table body
      if (i === "change" && ob["change"] === null) {
        result["change"] = null;
      }
      if (typeof ob[i] === "object" && !Array.isArray(ob[i])) {
        const temp = flatten(ob[i]);
        for (const j in temp) {
          result[j] = temp[j];
        }
      } else {
        result[i] = ob[i];
      }
    }
    return result;
  };

  // sort list by holding with most value
  const sortByValue = (a: Holdings, b: Holdings) => {
    if (a.value > b.value) return -1;
    return 0;
  };

  // SORT TABLE BODY DATA BY TOKEN WITH MOST MARKET VALUE
  const sorted = React.useMemo((): Holdings[] => {
    if (!holdings) return [];
    const flattenHoldings: Holdings[] = [];
    for (let i = 0; i < holdings.length; i++) {
      flattenHoldings.push(flatten(holdings[i]));
    }

    return flattenHoldings.sort(sortByValue);
  }, [holdings]);

  return (
    <ul id="wallets-holdings-tablelist" style={{ all: "unset" }}>
      {sorted.map((holding: Holding) => (
        <Grid
          component="li"
          item
          xs={12}
          container
          my={2}
          key={holding.code}
          className={classes.rowColor}>
          <Grid item container spacing={1} className={classes.listContainer}>
            <Grid
              item
              xs={2}
              container
              alignItems="center"
              justifyContent="center"
              style={{
                paddingRight: "2%",
              }}>
              <Grid item>
                <CountryOrCryptoFlag name={holding?.logoUrl || holding.code} size={32} />
              </Grid>
            </Grid>
            <Grid item xs={10} container justifyContent="center" alignItems="center">
              <Grid container>
                <Grid item xs={7}>
                  <Typography className={classes.title}>{formatCoinName(holding.name)}</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography className={classes.value} translate="no">
                    {formatCurrency(holding.totalValue, selectedCurrency)}
                  </Typography>
                </Grid>
              </Grid>

              <Grid item container>
                <Grid item xs={6} container>
                  <Typography className={classes.quantity} translate="no">
                    {roundNumber(holding.total)}
                  </Typography>
                  <Typography ml={1} className={classes.quantity}>
                    {holding.code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <GainsOrLoses balance={holding.change} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ))}
    </ul>
  );
};

export default HoldingsList;
